import { z } from 'zod';

export const WorkflowHiringNotificationsDetails = z.object({
  type: z.literal('hiringNotifications'),

  /**
   * The list of recipient emails to send hiring notification summaries to.
   */
  recipientEmails: z.string().email().array().optional(),

  /**
   * If this option is provided, only send notifications to this list of emails (debug mode).
   */
  forceOnlySendToThisEmail: z.string().optional(),

  /**
   * The number of previous days of applicants to consider for sending an email.
   */
  numPreviousDaysOfApplicants: z.number().optional(),

  /**
   * The minimum readiness rating for an applicant to be considered for an email.
   */
  minReadinessRating: z.number().optional(),

  /**
   * When true, include candidates that meet the resume score threshold (in addition to readiness rating).
   */
  resumeScoreEnabled: z.boolean().optional(),

  /**
   * Minimum resume score for a candidate to be included (only used when resumeScoreEnabled is true).
   */
  minResumeScore: z.number().optional(),

  /**
   * Number of days within which a previously-notified candidate appears in the "You may have missed" section.
   * Must be at least 1. Defaults to 7.
   */
  revisitWindowDays: z.number().min(1).optional(),

  /**
   * Maximum number of new candidates shown in the "New Top Candidates" section per email.
   * Defaults to 10.
   */
  maxNewCandidatesPerEmail: z.number().optional(),

  /**
   * Maximum number of revisit candidates shown in the "You may have missed" section per email.
   * Defaults to 5.
   */
  maxRevisitCandidatesShown: z.number().optional(),

  /**
   * Display name of the ATS shown in per-candidate links (e.g. "Lever", "Greenhouse").
   * Defaults to "ATS" if not set. Shown as "View in Lever" in notification emails.
   */
  atsName: z.string().optional(),

  /**
   * When set, resume scores are multiplied by this factor before display in the notification email.
   * Use 20 to convert a 0–5 scale to a 0–100 scale (consistent with ATS writeback overrides).
   * Filtering (minResumeScore) and HNES writeback are unaffected and remain on the raw scale.
   */
  resumeScoreDisplayMultiplier: z.number().positive().optional(),
});

export type WorkflowHiringNotificationsDetails = z.infer<
  typeof WorkflowHiringNotificationsDetails
>;
